import type { PxlIconName } from "#build/pxl-icons";

interface NavLink {
  type: "link" | "separator";
  label?: string;
  iconName?: PxlIconName;
  to?: string;
  hidden?: boolean;
}

export default function useNavbar() {
  const { t } = useI18n();
  const appConfig = useAppConfig();
  const logoPath = computed(() => (appConfig.useMVP ? "/events/" : "/"));
  const localePath = useLocalePath();
  const user = useUser();

  const links = computed<NavLink[]>(() => [
    {
      type: "link",
      label: t("navigation.label_users"),
      iconName: "users",
      to: localePath("/users/"),
      hidden: !user.value?.hasSuperAdminRole || !isFeatureEnabled("enable_user_management"),
    },
    {
      type: "link",
      label: t("navigation.label_my_organization"),
      iconName: "organization",
      to: localePath("/organizations/"),
      hidden: !(user.value?.hasSuperAdminRole || user.value?.hasOrganizationsAccess || user.value?.hasTenantAdminRole),
    },
    {
      type: "link",
      label: t("navigation.label_teams"),
      iconName: "teams",
      to: localePath("/teams/"),
    },
    { type: "separator" },
    {
      type: "link",
      label: t("navigation.label_videos"),
      iconName: "matches",
      to: localePath("/events/"),
    },
    {
      type: "link",
      label: t("navigation.label_my_highlights"),
      iconName: "my-highlights",
      to: localePath("/user/content/"),
    },
    { type: "separator" },
    {
      type: "link",
      label: t("navigation.label_reviews"),
      iconName: "review",
      to: localePath("/reviews/"),
      hidden: !isFeatureEnabled("enable_reviews"),
    },
    {
      type: "link",
      label: t("navigation.label_athletes"),
      iconName: "athletes",
      to: localePath("/athletes/"),
    },
    {
      type: "link",
      label: t("navigation.label_leagues"),
      iconName: "leagues",
      to: localePath("/leagues/"),
      hidden: !(user.value?.hasSuperAdminRole || user.value?.hasLeaguesAccess),
    },
    {
      type: "link",
      label: t("navigation.label_custom_tags"),
      iconName: "tools",
      to: (() => {
        const sportTypes = user.value?.teamsSports || [];

        if (sportTypes.length === 1 && !(user.value?.hasSuperAdminRole || user.value?.hasTenantAdminRole)) {
          return localePath(`/coaching-tools/custom-tags/${sportTypes[0]}/tag-groups`);
        }
        return localePath("/coaching-tools/custom-tags/");
      })(),
      hidden: !isFeatureEnabled("enable_custom_tags") || !user.value?.isCoachToolsEnabled,
    },
    {
      type: "link",
      label: t("navigation.label_my_profile"),
      iconName: "profile",
      to: localePath("/user/profile/"),
      hidden: appConfig.useMVP,
    },

    {
      type: "link",
      label: t("navigation.label_practices"),
      iconName: "practices",
      to: localePath("/practices/"),
      hidden: appConfig.useMVP,
    },
    {
      type: "link",
      label: t("navigation.label_highlights"),
      iconName: "highlights",
      to: localePath("/highlights/"),
      hidden: appConfig.useMVP,
    },
    { type: "separator", hidden: !canViewLoggingProfiles() },
    {
      type: "link",
      label: t("navigation.label_logging_profiles"),
      iconName: "list",
      to: localePath("/logging/profiles/"),
      hidden: !canViewLoggingProfiles(),
    },
    {
      type: "link",
      label: t("navigation.label_logging_jobs"),
      iconName: "list",
      to: localePath("/logging/jobs/"),
      hidden: !canViewLoggingJobs(),
    },
  ]);

  return { logoPath: localePath(logoPath.value), links };
}
